"use client"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Megaphone, X } from "lucide-react"
import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toEthiopian } from "ethiopian-calendar-new"
import { getEthiopianMonth } from "@/lib/ethiopian-date-conversion"

interface Announcement {
  id: string
  title: string
  title_am?: string
  body_main?: string
  body_main_am?: string
  body_followup?: string
  body_followup_am?: string
  start_date?: string
  end_date?: string
  priority?: number
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export default function AnnouncementSection() {
  const { i18n } = useTranslation()
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem('qetero.dismissedAnnouncements')
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem('qetero.dismissedAnnouncements', JSON.stringify(dismissed))
    } catch {}
  }, [dismissed])

  const { data: announcements } = useQuery({
    queryKey: ['announcements', i18n.language],
    queryFn: async () => {
      try {
        const res = await fetch(`https://qetero.com/api/v1/announcements/active?language=${i18n.language}`)
        if (!res.ok) return []
        const json = await res.json()
        return (json.data || []) as Announcement[]
      } catch (error) {
        console.error("Failed to fetch announcements", error)
        return []
      }
    },
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  const activeAnnouncements = announcements?.filter(a => !dismissed.includes(a.id) && a.is_active)


  if (!activeAnnouncements || activeAnnouncements.length === 0) return null

  return (
    <div className="w-full max-w-7xl mx-auto px-3 md:px-4 mb-2 animate-fade-in-up">
      <AnimatePresence>
        {activeAnnouncements.map((announcement) => (
          <motion.div
            key={announcement.id}
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: "hidden" }}
            transition={{ duration: 0.3 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 shadow-sm mb-3 last:mb-0"
          >
            <div className="p-4 flex gap-3 md:gap-4 items-start">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-10 h-10 rounded-full bg-white/60 border border-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
                  <Megaphone size={20} />
                </div>
              </div>
              <div className="flex-1 pt-0.5">
                <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1">
                  {i18n.language === 'am' && announcement.title_am ? announcement.title_am : announcement.title}
                </h3>
                { (announcement.start_date || announcement.end_date) && (
                  <div className="text-xs text-gray-500 mb-1">
                    {announcement.start_date && announcement.end_date ? (
                      i18n.language === 'am' ? (
                        // Ethiopian formatted range
                        (() => {
                          try {
                            const s = new Date(announcement.start_date as string)
                            const e = new Date(announcement.end_date as string)
                            const { year: sy, month: sm, day: sd } = toEthiopian(s.getFullYear(), s.getMonth()+1, s.getDate())
                            const { year: ey, month: em, day: ed } = toEthiopian(e.getFullYear(), e.getMonth()+1, e.getDate())
                            return `${sd} ${getEthiopianMonth(sm)} ${sy} — ${ed} ${getEthiopianMonth(em)} ${ey}`
                          } catch { return '' }
                        })()
                      ) : (
                        (() => {
                          try {
                            const s = new Date(announcement.start_date as string)
                            const e = new Date(announcement.end_date as string)
                            return `${s.toLocaleDateString()} — ${e.toLocaleDateString()}`
                          } catch { return '' }
                        })()
                      )
                    ) : (
                      announcement.start_date ? (new Date(announcement.start_date).toLocaleDateString()) : ''
                    )}
                  </div>
                )}

                <p className="text-xs md:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {i18n.language === 'am' && announcement.body_main_am ? announcement.body_main_am : announcement.body_main}
                </p>
              </div>
              <button 
                onClick={() => setDismissed(prev => [...prev, announcement.id])}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-amber-100 hover:text-amber-600 transition-colors -mr-1 -mt-1"
              >
                <X size={18} />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
